import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import policyService from '../../services/policy.service';
import type {
  AnalysisRuleViolation,
  AnalysisRuleViolationsResponse,
  RuleCategoryKey,
} from '../../types/policy.types';
import CategoryBadge from '../shared/CategoryBadge';
import RegulationCitation from '../shared/RegulationCitation';

interface AnalysisRuleViolationsPanelProps {
  analysisId: string;
}

type Filter = 'all' | RuleCategoryKey;

const CATEGORY_ORDER: RuleCategoryKey[] = ['compliance', 'legal_risk', 'qa'];

function formatSnapshotEntry(key: string, value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  return String(value ?? '—');
}

function formatEvidenceTimestamp(evidence: Record<string, unknown>): string | null {
  const startMs = evidence.start_ms;
  if (typeof startMs !== 'number') return null;
  const totalSec = Math.floor(startMs / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function AnalysisRuleViolationsPanel({ analysisId }: AnalysisRuleViolationsPanelProps) {
  const { t } = useTranslation('policy');
  const [data, setData] = useState<AnalysisRuleViolationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    policyService
      .getAnalysisRuleViolations(analysisId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError(t('errors.load', { defaultValue: 'Failed to load violations.' }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [analysisId, t]);

  const grouped = useMemo(() => {
    if (!data) return new Map<RuleCategoryKey, AnalysisRuleViolation[]>();
    const m = new Map<RuleCategoryKey, AnalysisRuleViolation[]>();
    for (const v of data.violations) {
      const list = m.get(v.category) ?? [];
      list.push(v);
      m.set(v.category, list);
    }
    return m;
  }, [data]);

  const visibleCategories = filter === 'all' ? CATEGORY_ORDER : [filter];
  const total = data?.violations.length ?? 0;

  if (loading) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
      </section>
    );
  }
  if (error) {
    return (
      <section className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        {error}
      </section>
    );
  }
  if (!data) return null;

  if (total === 0) {
    return (
      <section className="rounded-xl border border-green-200 bg-green-50 p-5">
        <div className="flex items-center gap-2 text-sm text-green-800">
          <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
          <span>
            {t('violations.none', {
              defaultValue: 'This call passed every active rule. No violations recorded.',
            })}
          </span>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-900">
            {t('violations.title', { defaultValue: 'Rules triggered on this call' })}
          </h2>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            {total}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            label={t('filters.all', { defaultValue: 'All' })}
            count={total}
          />
          {CATEGORY_ORDER.map((c) => {
            const count = data.by_category[c] ?? 0;
            if (count === 0) return null;
            return (
              <FilterChip
                key={c}
                active={filter === c}
                onClick={() => setFilter(c)}
                label={
                  <span className="inline-flex items-center gap-1">
                    <CategoryBadge category={c} size="xs" showIcon={false} />
                  </span>
                }
                count={count}
              />
            );
          })}
        </div>
      </header>

      <ul className="divide-y divide-gray-100">
        {visibleCategories.map((cat) => {
          const items = grouped.get(cat) ?? [];
          if (items.length === 0) return null;
          return items.map((v) => {
            const expanded = expandedId === v.id;
            const ts = formatEvidenceTimestamp(v.evidence);
            return (
              <li key={v.id} className="px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <CategoryBadge category={v.category} size="xs" />
                      <span className="font-medium text-gray-900">{v.rule_name}</span>
                      {ts && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
                          @ {ts}
                        </span>
                      )}
                      <span className="text-[11px] uppercase tracking-wide text-gray-500">
                        {t(`severity.${v.severity}`, { defaultValue: v.severity })}
                      </span>
                    </div>
                    {v.rule_description_short && (
                      <p className="mt-1 text-sm text-gray-600">{v.rule_description_short}</p>
                    )}
                    {v.regulation_refs.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {v.regulation_refs.map((reg) => (
                          <RegulationCitation
                            key={`${v.id}-${reg.jurisdiction}-${reg.law}`}
                            regulation={reg}
                            size="xs"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : v.id)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    {expanded
                      ? t('violations.hideCriterion', { defaultValue: 'Hide criterion' })
                      : t('violations.viewCriterion', { defaultValue: 'View criterion applied' })}
                    {expanded ? (
                      <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                </div>

                {expanded && (
                  <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {t('rule.appliedCriterion', { defaultValue: 'Criterion applied' })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('rule.appliedCriterionDescription', {
                        defaultValue: 'These were the values active when this call was evaluated.',
                      })}
                    </p>
                    {Object.keys(v.applied_config_snapshot).length === 0 ? (
                      <p className="mt-2 text-sm text-gray-600">
                        {t('violations.noTunable', {
                          defaultValue: 'This rule has no tunable parameters.',
                        })}
                      </p>
                    ) : (
                      <dl className="mt-2 space-y-1">
                        {Object.entries(v.applied_config_snapshot).map(([k, val]) => (
                          <div key={k} className="flex flex-wrap items-baseline gap-2">
                            <dt className="text-gray-600">{k}:</dt>
                            <dd className="font-mono font-semibold text-gray-900">
                              {formatSnapshotEntry(k, val)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}
              </li>
            );
          });
        })}
      </ul>

      <footer className="border-t border-gray-200 px-4 py-3 text-xs text-gray-500">
        {t('violations.footer', {
          defaultValue: 'Want to change which rules apply? See ',
        })}
        <Link to="/policy" className="font-medium text-indigo-600 hover:text-indigo-800">
          {t('violations.footerLink', { defaultValue: 'My Policy' })}
        </Link>
        .
      </footer>
    </section>
  );
}

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  label: React.ReactNode;
  count?: number;
}

function FilterChip({ active, onClick, label, count }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition ${
        active
          ? 'bg-indigo-600 text-white ring-indigo-600'
          : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
      }`}
    >
      {label}
      {typeof count === 'number' && (
        <span
          className={`rounded-full px-1.5 ${active ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
